import React, { FC, useState } from "react";
import { Grid } from "@mui/material";
import { TopLevelPaper } from "../../../../../../themes/StyledComponents";
import { useMasterTables } from "../../../../../../stores/MasterTables";
import BillBoxWrapper from "../../common/components/BillBoxWrapper";
import ScrollablePanel from "../../common/components/ScrollablePanel";
import TransactionRxOverview from "../../../Base/components/TransactionRxOverview";
import { GkvEditPageProps, DirectBillingRxDto } from "../types/interface";
import { colors } from "../../GKVNew/styles/tokens";
import GkvEditWorkspace from "./GkvEditWorkspace";

/**
 * Top-level page for the GKV Edit Bill workflow.
 *
 * Two-column layout:
 * - Left (5 cols): Edit workspace with bill cards, actions, stepper.
 * - Right (7 cols): Rx detail overview, shown when a row is selected.
 *
 * Entry point: receives `billIds` — fetches its own data via TanStack Query.
 */
const GkvEditPage: FC<GkvEditPageProps> = ({ billIds, onClose }) => {
  const { data: masterTablesData } = useMasterTables();

  const [selectedRx, setSelectedRx] = useState<any>(null);
  const [isDetailVisible, setIsDetailVisible] = useState(false);
  const [editRxMode, setEditRxMode] = useState(false);

  const handleRowClick = (rx: DirectBillingRxDto) => {
    setSelectedRx(rx);
    setIsDetailVisible(true);
  };

  const handleRxDelete = (deletedRxId: string) => {
    if (selectedRx?.id === deletedRxId) {
      setSelectedRx(null);
      setIsDetailVisible(false);
    }
  };

  return (
    <Grid container spacing={1} sx={{ height: "100%", marginTop: "1px", overflow: "hidden" }}>
      {/* Left panel — Edit workspace */}
      <BillBoxWrapper xs={5}>
        <TopLevelPaper
          sx={{
            padding: 0,
            backgroundColor: colors.rowHover,
            height: "calc(100vh - 64px)",
            overflow: "hidden",
          }}
        >
          <ScrollablePanel sx={{ padding: "0 0 12px 12px" }}>
            <GkvEditWorkspace
              billIds={billIds}
              onRowClick={handleRowClick}
              onRxDelete={handleRxDelete}
              onClose={onClose}
            />
          </ScrollablePanel>
        </TopLevelPaper>
      </BillBoxWrapper>

      {/* Right panel — Rx detail overview */}
      {isDetailVisible && (
        <BillBoxWrapper xs={7}>
          <TopLevelPaper sx={{ padding: 0, height: "calc(100vh - 64px)", overflow: "hidden" }}>
            <ScrollablePanel sx={{ padding: "12px" }}>
              <TransactionRxOverview
                rowData={selectedRx}
                masterData={masterTablesData}
                editRxMode={editRxMode}
                setEditRxMode={setEditRxMode}
              />
            </ScrollablePanel>
          </TopLevelPaper>
        </BillBoxWrapper>
      )}
    </Grid>
  );
};

export default GkvEditPage;
